import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download } from "lucide-react";
import { toast } from "sonner";

const BG = process.env.REACT_APP_AUTH_BG_IMAGE;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const prefillEmail = new URLSearchParams(window.location.search).get("email");
  const [email, setEmail] = useState(prefillEmail || "regulateur@pacemia.fr");
  const [password, setPassword] = useState(prefillEmail ? "" : "Pacemia2026!");
  const [loading, setLoading] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      toast.error(apiError(err, "Connexion impossible"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Logo size={34} className="mb-10" />
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Bureau de régulation</h1>
          <p className="text-muted-foreground mt-2 mb-8 text-sm">Connectez-vous à votre console PACEMIA.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" required className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" required className="mt-1.5" />
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
              {loading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>
          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-border" />
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">ou</span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <Button type="button" variant="outline" className="w-full gap-2.5" onClick={googleLogin} data-testid="login-google">
            <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.9z" />
              <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0 0 12 24z" />
              <path fill="#FBBC05" d="M5.4 14.4a7.2 7.2 0 0 1 0-4.6V6.7H1.4a12 12 0 0 0 0 10.8l4-3.1z" />
              <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.4 6.7l4 3.1C6.3 6.9 8.9 4.8 12 4.8z" />
            </svg>
            Continuer avec Google
          </Button>
          <p className="text-[11px] text-muted-foreground mt-2">
            Réservé aux gérants, personnels de régulation et chauffeurs.
          </p>
          <p className="text-sm text-muted-foreground mt-6">
            Nouveau gérant ?{" "}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="link-register">
              Démarrer l'essai gratuit
            </Link>
          </p>
          <p className="mt-3">
            <Link to="/install" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary hover:underline" data-testid="login-install-link">
              <Download className="h-3.5 w-3.5" /> Installer l'application sur mobile (chauffeurs)
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative">
        <img src={BG} alt="Ambulance" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-sky-900/90 via-sky-800/40 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-display text-3xl font-bold leading-tight">Régulez au rythme du réseau.</p>
        </div>
      </div>
    </div>
  );
}
